import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { catchError, exhaustMap, share, switchMap, tap } from 'rxjs/operators';
import { SharedActions } from '@states/shared/shared.action-types';
import { AnalyticsSyncActions } from '@states/analytics-sync/analytics-sync.action-types';
import { AnalyticsSyncService } from '../../services/analytics-sync.service';
import { of } from 'rxjs';

@Injectable()
export class AnalyticsSyncEffects {
  public startTriggerSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsSyncActions.startTriggerSync),
      exhaustMap(() => [
        AnalyticsSyncActions.setSyncTriggering({ isTriggering: true }),
        AnalyticsSyncActions.triggerSync(),
      ]),
    ),
  );

  public triggerSync$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsSyncActions.triggerSync),
      switchMap(() => {
        return this.analyticsSyncService.triggerSync()
          .pipe(
            switchMap(syncResponse => [
              AnalyticsSyncActions.triggerSyncSuccess({ syncResponse }),
              AnalyticsSyncActions.setSyncTriggering({ isTriggering: false }),
              SharedActions.showMessage({ success: 'Analytics sync triggered successfully' }),
              // Refresh logs after successful sync
              AnalyticsSyncActions.startGetSyncLogs(),
            ]),
            catchError(error => [
              AnalyticsSyncActions.triggerSyncFail(),
              SharedActions.showMessage({ error: 'Failed to trigger analytics sync' }),
              AnalyticsSyncActions.setSyncTriggering({ isTriggering: false }),
            ]),
          );
      }),
      share(),
    ),
  );

  public startGetSyncLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsSyncActions.startGetSyncLogs),
      exhaustMap(() => [AnalyticsSyncActions.getSyncLogs({ limit: 20 })]),
    ),
  );

  public getSyncLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsSyncActions.getSyncLogs),
      switchMap(({ limit = 20 }) => {
        return this.analyticsSyncService.getSyncLogs(limit)
          .pipe(
            switchMap(syncLogs => [AnalyticsSyncActions.getSyncLogsSuccess({ syncLogs })]),
            catchError(error => [
              AnalyticsSyncActions.getSyncLogsFail(),
              SharedActions.consoleMessage({ error: 'Failed to load sync logs' }),
            ]),
          );
      }),
      share(),
    ),
  );

  public startGetSyncStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsSyncActions.startGetSyncStats),
      exhaustMap(() => [AnalyticsSyncActions.getSyncStats()]),
    ),
  );

  public getSyncStats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsSyncActions.getSyncStats),
      switchMap(() => {
        return this.analyticsSyncService.getSyncStats()
          .pipe(
            switchMap(syncStats => [AnalyticsSyncActions.getSyncStatsSuccess({ syncStats })]),
            catchError(error => [
              AnalyticsSyncActions.getSyncStatsFail(),
              SharedActions.consoleMessage({ error: 'Failed to load sync stats' }),
            ]),
          );
      }),
      share(),
    ),
  );

  public refreshAnalyticsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsSyncActions.refreshAnalyticsData),
      exhaustMap(() => [
        AnalyticsSyncActions.startGetSyncLogs(),
        AnalyticsSyncActions.startGetSyncStats(),
      ]),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private analyticsSyncService: AnalyticsSyncService,
  ) {
  }
}
